import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import styles from './index.module.scss';
import { useLang } from '@/i18n';
import { createEFPic } from '@/utils/useEFPic';
import EFVideo from '@/components/UI/EFVideo';

const { EFPic } = createEFPic('/media/power-stream/teaser/pc/');

export default function SectionE() {
    const { t } = useLang('powerstream.detail.sectionE');
    const boxRef = useRef<HTMLDivElement>(null);
    const { locale } = useRouter();
    const imgLocale = ['uk', 'es', 'it', 'de', 'fr'].includes(locale as string) ? locale : 'uk';
    const videoRef = useRef<HTMLVideoElement>(null);

    const data = [
        {
            title: t('sec2.monitoring.title'),
            desc: 'sec2.monitoring.desc',
            bg: [`sectionE_bg_1.webp`, `sectionE_bg_1.jpg`],
            phone: [`sectionE_phone_1_${imgLocale}.webp`, `sectionE_phone_1_${imgLocale}.png`],
            class: 'real-time',
        },
        {
            title: t('sec2.remote.title'),
            desc: 'sec2.remote.desc',
            bg: [`sectionE_bg_2.webp`, `sectionE_bg_2.jpg`],
            phone: [`sectionE_phone_2_${imgLocale}.webp`, `sectionE_phone_2_${imgLocale}.png`],
            class: 'remote-control',
        },
        {
            title: t('sec2.allocation.title'),
            desc: 'sec2.allocation.desc',
            bg: [`sectionE_bg_3.webp`, `sectionE_bg_3.jpg`],
            phone: [`sectionE_phone_3_${imgLocale}.webp`, `sectionE_phone_3_${imgLocale}.png`],
            class: 'smartly-allocation',
        },
    ];

    useEffect(() => {
        const videoDom: any = videoRef.current;
        const boxDom: any = boxRef.current;
        const pageHeight = window.innerHeight;
        const top = 62;
        if (pageHeight < 850) {
            const scale = pageHeight / 1000;
            (boxDom as HTMLDivElement).style.cssText = `transform:scale(${scale}); top: -${top}px; min-height: ${
                document.documentElement.clientHeight + top
            }px`;
        }
        AniContainer.create({
            triggerElement: '.e-section .img-box',
            offset: '40%',
            duration: 0,
            triggerHook: 1,
            onEnter: () => {
                videoDom && videoDom.play();
            },
        });

        AniContainer.create({
            asyncFromTo: [
                ['.e-sec2__box .real-time .title', { opacity: 1, y: 0 }, { opacity: 0, y: -20 }, 0.5], // start: 0 end: 0.5
                ['.e-sec2__box .real-time .desc', { opacity: 1, y: 0 }, { opacity: 0, y: -20 }, 0.5],
                ['.e-sec2__box .real-time .phone-img', { opacity: 1 }, { opacity: 0 }, 0.5],
                ['.e-sec2__box .scene-img-box', { x: 0 }, { x: -880 }, 0.5],
                ['.e-sec2__box .remote-control .title', { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 0.5], // start: 0.2 end: 0.7
                ['.e-sec2__box .remote-control .desc', { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 0.5],
                ['.e-sec2__box .remote-control .phone-img', { opacity: 0 }, { opacity: 1 }, 0.5],
                ['.e-sec2__box .remote-control .title', { opacity: 1, y: 0 }, { opacity: 0, y: -20 }, 0.5], // start: 1  end: 1.5
                ['.e-sec2__box .remote-control .desc', { opacity: 1, y: 0 }, { opacity: 0, y: -20 }, 0.5],
                ['.e-sec2__box .remote-control .phone-img', { opacity: 1 }, { opacity: 0 }, 0.5],
                ['.e-sec2__box .scene-img-box', { x: -880 }, { x: -880 * 2 }, 0.5],
                ['.e-sec2__box .smartly-allocation .title', { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 0.5], // start: 1.2 end: 1.7
                ['.e-sec2__box .smartly-allocation .desc', { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 0.5],
                ['.e-sec2__box .smartly-allocation .phone-img', { opacity: 0 }, { opacity: 1 }, 0.5],
            ],
            triggerElement: '.e-sec2__box',
            offset: '150%',
            duration: '250%',
            triggerHook: 1,
            asyncTimeLine: [0, 0, 0, 0, 0.4, 0.4, 0.2, 1.2, 1.2, 1, 1.2, 1.6, 1.6, 1.2],
        });
    }, []);

    return (
        <div className={styles.sectionE}>
            <div className="e-section">
                <div className="e-sec1__box">
                    <div className="sec1-box">
                        {/*<p className="sub-title">{t('sec1.subTitle')}</p>*/}
                        {/*<p className="title">{t('sec1.title')}</p>*/}
                        <div className="img-box">
                            <EFVideo
                                muted
                                ref={videoRef}
                                autoPlay={false}
                                loop={false}
                                showReplay
                                className="img-bg"
                                sources={['/media/power-stream/teaser/pc/sectionE.webm', '/media/power-stream/teaser/pc/sectionE.mp4']}
                            />
                        </div>
                    </div>
                </div>
                <div className="e-sec2__box" ref={boxRef}>
                    {data.map((item: any, index) => (
                        <div key={index} className={`sec2-common ${item.class}`}>
                            <div className="sec2-content">
                                <div className="text-box">
                                    <p className="title">{item.title}</p>
                                    <p className="desc">{t.rich(item.desc, { HighLight: (text: any) => <span className="green-text">{text}</span> })}</p>
                                </div>
                                <div className="phone-box">
                                    <EFPic className="phone-img" name={item.phone} />
                                    <EFPic className="phone-blank-img" name={['phone--blank-img.png', 'phone--blank-img.png']} />
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="img-container">
                        <div className="img-box-1">
                            <div className="scene-img-box">
                                {data.map((item: any, index) => (
                                    <EFPic className={`bottom-img ${item.class}_scene_img`} key={index} name={item.bg} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
